/**
 * 客服管理 API 服務
 * 使用 RTK Query 封裝客服管理相關的 API 調用
 */

import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithErrorHandling } from '@/services/baseApi';
import {
  BatchCancelVerificationRequest,
  BatchCancelVerificationResponse,
  BatchRefundRequest,
  BatchRefundResponse,
  BatchSupplementaryVerificationRequest,
  BatchSupplementaryVerificationResponse,
  CancelVerificationRequest,
  CancelVerificationResponse,
  PaginatedResponse,
  RefundAmountInfo,
  RefundRequest,
  RefundResponse,
  SupplementaryVerificationRequest,
  SupplementaryVerificationResponse,
  TicketListParams,
  TicketSearchParams,
  TicketSearchResponse,
  UnusedTicket,
  VerifiedTicket,
} from '@/types/customerService';
import { ConstantUtil } from '@/utils/ConstantUtil';

// 創建 API 服務
export const customerServiceApi = createApi({
  reducerPath: 'customerServiceApi',
  baseQuery: baseQueryWithErrorHandling,
  // baseQuery, // 真實 API baseQuery（待 API 整合後使用）
  tagTypes: ['Ticket', 'Verification', 'Refund'],
  endpoints: (builder) => ({
    // 獲取未使用票券列表（補核銷頁面）
    getUnusedTickets: builder.query<PaginatedResponse<UnusedTicket>, TicketListParams>({
      query: (params) => ({
        url: ConstantUtil.API_CUSTOMER_SERVICE_UNUSED_TICKETS,
        method: 'GET',
        params: {
          page: params.page,
          pageSize: params.pageSize,
          ...(params.searchKeyword && { searchKeyword: params.searchKeyword }),
          status: 'unused',
        },
      }),
      providesTags: ['Ticket'],
    }),

    // 獲取已核銷但未請款票券列表（取消核銷頁面）
    getVerifiedTickets: builder.query<PaginatedResponse<VerifiedTicket>, TicketListParams>({
      query: (params) => ({
        url: ConstantUtil.API_CUSTOMER_SERVICE_VERIFIED_TICKETS,
        method: 'GET',
        params: {
          page: params.page,
          pageSize: params.pageSize,
          ...(params.searchKeyword && { searchKeyword: params.searchKeyword }),
          ...(params.paymentStatus && { paymentStatus: params.paymentStatus }),
        },
      }),
      providesTags: ['Ticket'],
    }),

    // 補核銷操作
    supplementaryVerification: builder.mutation<
      SupplementaryVerificationResponse,
      SupplementaryVerificationRequest
    >({
      query: (data) => ({
        url: ConstantUtil.API_CUSTOMER_SERVICE_SUPPLEMENTARY_VERIFICATION,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Ticket', 'Verification'],
    }),

    // 取消核銷操作
    cancelVerification: builder.mutation<
      CancelVerificationResponse,
      CancelVerificationRequest
    >({
      query: (data) => ({
        url: ConstantUtil.API_CUSTOMER_SERVICE_CANCEL_VERIFICATION,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Ticket', 'Verification'],
    }),

    // 計算可退款金額
    getRefundAmount: builder.query<RefundAmountInfo, string>({
      query: (ticketId) => ({
        url: `${ConstantUtil.API_CUSTOMER_SERVICE_REFUND_AMOUNT}/${ticketId}`,
        method: 'GET',
      }),
      providesTags: ['Refund'],
    }),

    // 退款操作
    refund: builder.mutation<RefundResponse, RefundRequest>({
      query: (data) => ({
        url: ConstantUtil.API_CUSTOMER_SERVICE_REFUND,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Ticket', 'Refund'],
    }),

    // 票券查詢
    searchTickets: builder.query<TicketSearchResponse, TicketSearchParams>({
      query: (params) => ({
        url: ConstantUtil.API_CUSTOMER_SERVICE_TICKET_SEARCH,
        method: 'GET',
        params: {
          ...(params.ticketNumber && { ticketNumber: params.ticketNumber }),
          ...(params.merchantName && { merchantName: params.merchantName }),
          ...(params.verificationDateStart && { verificationDateStart: params.verificationDateStart }),
          ...(params.verificationDateEnd && { verificationDateEnd: params.verificationDateEnd }),
          ...(params.purchaseDateStart && { purchaseDateStart: params.purchaseDateStart }),
          ...(params.purchaseDateEnd && { purchaseDateEnd: params.purchaseDateEnd }),
          page: params.page ?? 1,
          pageSize: params.pageSize ?? ConstantUtil.PAGE_SIZE,
        },
      }),
      providesTags: ['Ticket'],
    }),

    // 批量退款
    batchRefund: builder.mutation<BatchRefundResponse, BatchRefundRequest>({
      query: (data) => ({
        url: ConstantUtil.API_CUSTOMER_SERVICE_BATCH_REFUND,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Ticket', 'Refund'],
    }),

    // 批量取消核銷
    batchCancelVerification: builder.mutation<
      BatchCancelVerificationResponse,
      BatchCancelVerificationRequest
    >({
      query: (data) => ({
        url: ConstantUtil.API_CUSTOMER_SERVICE_BATCH_CANCEL_VERIFICATION,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Ticket', 'Verification'],
    }),

    // 批量補核銷
    batchSupplementaryVerification: builder.mutation<
      BatchSupplementaryVerificationResponse,
      BatchSupplementaryVerificationRequest
    >({
      query: (data) => ({
        url: ConstantUtil.API_CUSTOMER_SERVICE_BATCH_SUPPLEMENTARY_VERIFICATION,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Ticket', 'Verification'],
    }),
  }),
});

// 導出自動生成的 hooks
export const {
  useGetUnusedTicketsQuery,
  useGetVerifiedTicketsQuery,
  useSupplementaryVerificationMutation,
  useCancelVerificationMutation,
  useGetRefundAmountQuery,
  useRefundMutation,
  useSearchTicketsQuery,
  useBatchRefundMutation,
  useBatchCancelVerificationMutation,
  useBatchSupplementaryVerificationMutation,
} = customerServiceApi;

